import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CommentDto, CreateCommentInput, MeDto } from '@zoxo/shared';

import { api } from '../../lib/api';
import { useMe } from '../auth/use-me';
import { commentsKey, commentsPath, type CommentTarget } from './comment-target';

/**
 * Comment write mutations (POST {base}/comments, PATCH/DELETE /comments/:id,
 * AC-3). Plain mutations (frontend rule). The thread is shared by surveys and
 * PD 수첩 articles (V4) — `target` selects the endpoint + cache key. On success
 * the target's comment list is invalidated; ownership of edit/delete is
 * server-enforced (isMine drives only button visibility).
 */

interface MutationContext {
  previous?: CommentDto[];
  tempId: string;
}

/**
 * Create (top-level or reply) with OPTIMISTIC update: the new comment appears in
 * the target's comment cache the instant it's submitted, attributed to the
 * sender (me's nickname — anonymity-safe). On success we swap the
 * temp item for the server's real one; onError rolls the cache back. onSettled
 * keeps the authoritative invalidate so server-side fields (replyCount, ids)
 * reconcile.
 */
export function useCreateComment(target: CommentTarget) {
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const queryKey = commentsKey(target);

  return useMutation<CommentDto, unknown, CreateCommentInput, MutationContext>({
    mutationFn: (input: CreateCommentInput) =>
      api.post<CommentDto>(commentsPath(target), input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CommentDto[]>(queryKey);
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimistic = buildOptimisticComment(tempId, input, me);

      queryClient.setQueryData<CommentDto[]>(queryKey, (old) => {
        const next = old ? [...old, optimistic] : [optimistic];
        // Keep the parent's replyCount in sync so the toggle shows immediately.
        if (input.parentId) {
          return next.map((c) =>
            c.id === input.parentId
              ? { ...c, replyCount: (c.replyCount ?? 0) + 1 }
              : c,
          );
        }
        return next;
      });

      return { previous, tempId };
    },
    onSuccess: (created, _input, ctx) => {
      // Swap the temp placeholder for the authoritative server record.
      queryClient.setQueryData<CommentDto[]>(
        queryKey,
        (old) =>
          old?.map((c) => (ctx && c.id === ctx.tempId ? created : c)) ?? old,
      );
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

/** Build the temporary comment shown until the server responds. */
function buildOptimisticComment(
  tempId: string,
  input: CreateCommentInput,
  me: MeDto | undefined,
): CommentDto {
  const now = new Date().toISOString();
  return {
    id: tempId,
    body: input.body,
    parentId: input.parentId ?? null,
    author: {
      nickname: me?.nickname ?? '나',
      // 낙관적 표시라 정확한 총량을 모른다. 최소 1 — 방금 쓴 이 댓글이다.
      // 서버 응답이 오면 실제 값으로 맞춰진다.
      commentCount: 1,
    },
    isMine: true,
    ...(input.parentId ? {} : { replyCount: 0 }),
    createdAt: now,
    updatedAt: now,
  };
}

export function useUpdateComment(target: CommentTarget, commentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommentInput) =>
      api.patch<CommentDto>(`/comments/${commentId}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentsKey(target) });
    },
  });
}

export function useDeleteComment(target: CommentTarget, commentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>(`/comments/${commentId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentsKey(target) });
    },
  });
}
